import { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import SelectField from "components/admin/SelectField";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate } from "utils/format";

const STATUS_OPEN = 0;
const STATUS_RESOLVED = 1;
const STATUS_IGNORED = 2;
const STATUS_ALL = -1;

const STATUS_LABEL = {
  [STATUS_OPEN]: { label: "Đang mở", color: "error" },
  [STATUS_RESOLVED]: { label: "Đã xử lý", color: "success" },
  [STATUS_IGNORED]: { label: "Bỏ qua", color: "default" },
};

const LEVEL_LABEL = {
  1: { label: "Cảnh báo", color: "warning" },
  2: { label: "Lỗi", color: "error" },
};

const REFRESH_MS = 30000;
const EMPTY_FILTER = { status: STATUS_OPEN, level: 0, q: "", sort: "last" };

function LevelChip({ level }) {
  const info = LEVEL_LABEL[level] || LEVEL_LABEL[2];
  return <Chip size="small" color={info.color} label={info.label} />;
}

LevelChip.propTypes = { level: PropTypes.number.isRequired };

function StatusChip({ status, reopenCount }) {
  const info = STATUS_LABEL[status] || STATUS_LABEL[STATUS_OPEN];
  return (
    <MDBox display="flex" gap={0.5} flexWrap="wrap">
      <Chip size="small" color={info.color} variant="outlined" label={info.label} />
      {status === STATUS_OPEN && reopenCount > 0 && (
        <Chip size="small" color="warning" label={`Tái phát x${reopenCount}`} />
      )}
    </MDBox>
  );
}

StatusChip.propTypes = {
  status: PropTypes.number.isRequired,
  reopenCount: PropTypes.number,
};

StatusChip.defaultProps = { reopenCount: 0 };

function PlayerLink({ userId, name }) {
  if (!userId) return "-";
  return (
    <Link to={`/players/${userId}`}>
      <MDTypography variant="button" color="info">
        {name || `#${userId}`}
      </MDTypography>
    </Link>
  );
}

PlayerLink.propTypes = { userId: PropTypes.number, name: PropTypes.string };
PlayerLink.defaultProps = { userId: 0, name: "" };

function Stat({ label, value, color }) {
  return (
    <MDBox px={2} py={1} borderRadius="md" sx={{ border: 1, borderColor: "grey.300" }}>
      <MDTypography variant="h5" color={color}>
        {value ?? "-"}
      </MDTypography>
      <MDTypography variant="caption" color="text">
        {label}
      </MDTypography>
    </MDBox>
  );
}

Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  color: PropTypes.string,
};

Stat.defaultProps = { value: null, color: "dark" };

/** Chi tiết một nhóm lỗi: stack mẫu, người chơi gặp lỗi, các lần xảy ra gần nhất. */
function ErrorDetail({ id, canEdit, onClose, onChanged }) {
  const { data, loading, error, reload } = useFetch(() => api.get(`/api/admin/errors/${id}`), [id]);
  const [run, busy] = useRunner();
  const [note, setNote] = useState("");
  const group = data?.group;

  const setStatus = async (status, message) => {
    const res = await run(
      () => api.post(`/api/admin/errors/${id}/status`, { status, note }),
      message
    );
    if (res.ok) {
      setNote("");
      reload();
      onChanged();
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 2 }}>
        {group ? (
          <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <LevelChip level={group.level} />
            <StatusChip status={group.status} reopenCount={group.reopenCount} />
            <MDTypography variant="h6" sx={{ wordBreak: "break-word" }}>
              {group.title}
            </MDTypography>
          </MDBox>
        ) : (
          "Chi tiết lỗi"
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && !data && (
          <MDBox display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </MDBox>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {group && (
          <MDBox display="flex" flexDirection="column" gap={2}>
            <MDTypography variant="button" color="text">
              Nguồn <b>{group.source || "-"}</b> · {group.count.toLocaleString("vi-VN")} lần · lần
              đầu {formatDate(group.firstSeen)} · lần cuối {formatDate(group.lastSeen)}
              {group.resolvedAt && (
                <>
                  {" "}
                  · {group.status === STATUS_IGNORED ? "bỏ qua" : "xử lý"} lúc{" "}
                  {formatDate(group.resolvedAt)} bởi {group.resolvedBy || "-"}
                </>
              )}
            </MDTypography>
            {group.note && <Alert severity="info">Ghi chú: {group.note}</Alert>}

            <MDBox>
              <MDTypography variant="button" fontWeight="bold">
                Nội dung / stack trace (lần gần nhất)
              </MDTypography>
              <MDBox
                component="pre"
                mt={1}
                p={1.5}
                borderRadius="md"
                sx={{
                  bgcolor: "grey.100",
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  maxHeight: 320,
                  overflow: "auto",
                  m: 0,
                }}
              >
                {group.sample}
              </MDBox>
            </MDBox>

            <MDBox>
              <MDTypography variant="button" fontWeight="bold">
                Người chơi gặp lỗi ({group.players}) — trong {data.keepEventDays} ngày gần nhất
              </MDTypography>
              {data.players.length === 0 ? (
                <MDTypography variant="caption" color="text" display="block">
                  Không gắn với người chơi nào (lỗi ở luồng nền: map, boss, lưu dữ liệu...).
                </MDTypography>
              ) : (
                <AdminTable
                  rows={data.players}
                  rowKey="userId"
                  columns={[
                    {
                      key: "playerName",
                      label: "Nhân vật",
                      render: (r) => <PlayerLink userId={r.userId} name={r.playerName} />,
                    },
                    { key: "userId", label: "user_id", width: 90 },
                    { key: "times", label: "Số lần", width: 80 },
                    { key: "lastTime", label: "Lần cuối", render: (r) => formatDate(r.lastTime) },
                  ]}
                />
              )}
            </MDBox>

            <MDBox>
              <MDTypography variant="button" fontWeight="bold">
                Các lần xảy ra gần nhất ({data.events.length})
              </MDTypography>
              <AdminTable
                rows={data.events}
                rowKey="id"
                emptyText="Chi tiết từng lần đã quá hạn lưu"
                columns={[
                  {
                    key: "time",
                    label: "Thời gian",
                    width: 170,
                    render: (r) => formatDate(r.time),
                  },
                  {
                    key: "player",
                    label: "Nhân vật",
                    width: 160,
                    render: (r) => <PlayerLink userId={r.userId} name={r.playerName} />,
                  },
                  {
                    key: "message",
                    label: "Dòng đầu",
                    wrap: true,
                    render: (r) => (r.message || "").split("\n")[0],
                  },
                ]}
              />
              <MDTypography variant="caption" color="text">
                Lỗi dồn dập chỉ lưu tối đa 20 dòng chi tiết mỗi 5 giây, cột &ldquo;Số lần&rdquo; vẫn
                đếm đủ.
              </MDTypography>
            </MDBox>

            {canEdit && (
              <MDInput
                fullWidth
                label="Ghi chú khi đổi trạng thái (tuỳ chọn), vd: đã sửa ở commit abc123"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                inputProps={{ maxLength: 500 }}
              />
            )}
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        {group && canEdit && group.status !== STATUS_RESOLVED && (
          <MDButton
            color="success"
            variant="gradient"
            disabled={busy}
            onClick={() => setStatus(STATUS_RESOLVED, "Đã đánh dấu đã xử lý")}
          >
            Đã xử lý
          </MDButton>
        )}
        {group && canEdit && group.status !== STATUS_IGNORED && (
          <MDButton
            color="secondary"
            variant="outlined"
            disabled={busy}
            onClick={() => setStatus(STATUS_IGNORED, "Đã bỏ qua nhóm lỗi này")}
          >
            Bỏ qua
          </MDButton>
        )}
        {group && canEdit && group.status !== STATUS_OPEN && (
          <MDButton
            color="warning"
            variant="outlined"
            disabled={busy}
            onClick={() => setStatus(STATUS_OPEN, "Đã mở lại")}
          >
            Mở lại
          </MDButton>
        )}
        <MDButton color="dark" variant="text" disabled={busy} onClick={onClose}>
          Đóng
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

ErrorDetail.propTypes = {
  id: PropTypes.number.isRequired,
  canEdit: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
};

function ServerErrors() {
  const canEdit = useCan(PERMISSIONS.ERROR_EDIT);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [applied, setApplied] = useState(EMPTY_FILTER);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [run, busy] = useRunner();

  const summary = useFetch(() => api.get("/api/admin/errors/summary"), [], REFRESH_MS);
  const list = useFetch(
    () => api.get("/api/admin/errors", { ...applied, page, size }),
    [applied, page, size],
    REFRESH_MS
  );
  const rows = list.data?.items || [];

  const refresh = () => {
    summary.reload();
    list.reload();
  };

  const apply = (next) => {
    setFilter(next);
    setApplied(next);
    setPage(1);
    setSelected([]);
  };

  const set = (key) => (e) => {
    const next = { ...filter, [key]: e.target.value };
    // Ô chọn áp dụng ngay, ô tìm chữ đợi bấm Lọc / Enter.
    if (key === "q") setFilter(next);
    else apply(next);
  };

  const bulk = async (status, label) => {
    const res = await run(
      () => api.post("/api/admin/errors/status", { ids: selected, status }),
      (data) => `${label} ${data.changed} nhóm lỗi`
    );
    if (res.ok) {
      setSelected([]);
      refresh();
    }
  };

  const toggle = (id) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const allOnPage = rows.length > 0 && rows.every((r) => selected.includes(r.id));

  const s = summary.data;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="errors" />
        <PageCard
          title="Lỗi máy chủ"
          subtitle={`Cảnh báo / lỗi của GServer gộp theo nhóm, tự làm mới mỗi ${
            REFRESH_MS / 1000
          } giây`}
          color="error"
          noPadding
          actions={
            <MDButton variant="outlined" color="white" size="small" onClick={refresh}>
              Làm mới
            </MDButton>
          }
        >
          <MDBox px={2} pb={1} display="flex" gap={1.5} flexWrap="wrap">
            <Stat label="Nhóm đang mở" value={s?.open} color="error" />
            <Stat label="Trong đó mức Lỗi" value={s?.openErrors} color="error" />
            <Stat label="Đang mở, có trong 24h" value={s?.active24h} color="warning" />
            <Stat label="Nhóm mới trong 24h" value={s?.new24h} color="info" />
            <Stat label="Tái phát sau khi xử lý" value={s?.reopened} color="warning" />
          </MDBox>
          <MDBox
            px={2}
            pb={1}
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              apply({ ...filter });
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} md={2}>
                <SelectField
                  fullWidth
                  label="Trạng thái"
                  value={filter.status}
                  onChange={set("status")}
                >
                  <MenuItem value={STATUS_OPEN}>Đang mở</MenuItem>
                  <MenuItem value={STATUS_RESOLVED}>Đã xử lý</MenuItem>
                  <MenuItem value={STATUS_IGNORED}>Bỏ qua</MenuItem>
                  <MenuItem value={STATUS_ALL}>Tất cả</MenuItem>
                </SelectField>
              </Grid>
              <Grid item xs={6} md={2}>
                <SelectField fullWidth label="Mức" value={filter.level} onChange={set("level")}>
                  <MenuItem value={0}>Tất cả</MenuItem>
                  <MenuItem value={2}>Lỗi</MenuItem>
                  <MenuItem value={1}>Cảnh báo</MenuItem>
                </SelectField>
              </Grid>
              <Grid item xs={6} md={2}>
                <SelectField fullWidth label="Sắp xếp" value={filter.sort} onChange={set("sort")}>
                  <MenuItem value="last">Lần cuối mới nhất</MenuItem>
                  <MenuItem value="count">Nhiều lần nhất</MenuItem>
                  <MenuItem value="first">Mới xuất hiện</MenuItem>
                </SelectField>
              </Grid>
              <Grid item xs={12} md={4}>
                <MDInput
                  fullWidth
                  label="Tìm trong nội dung / stack (vd: grantTitleCard)"
                  value={filter.q}
                  onChange={set("q")}
                />
              </Grid>
              <Grid item xs="auto">
                <MDButton type="submit" variant="gradient" color="info">
                  Lọc
                </MDButton>
              </Grid>
            </Grid>
            {(list.error || summary.error) && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {list.error || summary.error}
              </Alert>
            )}
            {canEdit && selected.length > 0 && (
              <MDBox mt={1.5} display="flex" gap={1} alignItems="center" flexWrap="wrap">
                <MDTypography variant="button">Đã chọn {selected.length} nhóm:</MDTypography>
                <MDButton
                  size="small"
                  color="success"
                  variant="gradient"
                  disabled={busy}
                  onClick={() => bulk(STATUS_RESOLVED, "Đã đánh dấu xử lý")}
                >
                  Đã xử lý
                </MDButton>
                <MDButton
                  size="small"
                  color="secondary"
                  variant="outlined"
                  disabled={busy}
                  onClick={() => bulk(STATUS_IGNORED, "Đã bỏ qua")}
                >
                  Bỏ qua
                </MDButton>
                <MDButton
                  size="small"
                  color="warning"
                  variant="outlined"
                  disabled={busy}
                  onClick={() => bulk(STATUS_OPEN, "Đã mở lại")}
                >
                  Mở lại
                </MDButton>
                <MDButton size="small" variant="text" color="dark" onClick={() => setSelected([])}>
                  Bỏ chọn
                </MDButton>
              </MDBox>
            )}
          </MDBox>
          <AdminTable
            loading={list.loading && !list.data}
            rows={rows}
            rowKey="id"
            page={page}
            size={size}
            total={list.data?.total}
            onPageChange={setPage}
            onSizeChange={(v) => {
              setSize(v);
              setPage(1);
            }}
            onRowClick={(r) => setOpenId(r.id)}
            emptyText={
              applied.status === STATUS_OPEN
                ? "Không có lỗi nào đang mở 🎉"
                : "Không có nhóm lỗi nào"
            }
            columns={[
              ...(canEdit
                ? [
                    {
                      key: "select",
                      label: (
                        <Checkbox
                          size="small"
                          checked={allOnPage}
                          onChange={() =>
                            setSelected(
                              allOnPage
                                ? []
                                : Array.from(new Set([...selected, ...rows.map((r) => r.id)]))
                            )
                          }
                        />
                      ),
                      width: 48,
                      render: (r) => (
                        <Checkbox
                          size="small"
                          checked={selected.includes(r.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggle(r.id)}
                        />
                      ),
                    },
                  ]
                : []),
              {
                key: "level",
                label: "Mức",
                width: 100,
                render: (r) => <LevelChip level={r.level} />,
              },
              {
                key: "title",
                label: "Lỗi",
                wrap: true,
                render: (r) => (
                  <MDBox>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {r.title}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block">
                      {r.source}
                      {r.note ? ` · ${r.note}` : ""}
                    </MDTypography>
                  </MDBox>
                ),
              },
              {
                key: "count",
                label: "Số lần",
                width: 90,
                render: (r) => r.count.toLocaleString("vi-VN"),
              },
              { key: "players", label: "Người chơi", width: 100 },
              {
                key: "lastSeen",
                label: "Lần cuối",
                width: 170,
                render: (r) => formatDate(r.lastSeen),
              },
              {
                key: "firstSeen",
                label: "Lần đầu",
                width: 170,
                render: (r) => formatDate(r.firstSeen),
              },
              {
                key: "status",
                label: "Trạng thái",
                width: 150,
                render: (r) => <StatusChip status={r.status} reopenCount={r.reopenCount} />,
              },
            ]}
          />
        </PageCard>
        {openId != null && (
          <ErrorDetail
            id={openId}
            canEdit={canEdit}
            onClose={() => setOpenId(null)}
            onChanged={refresh}
          />
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ServerErrors;
